/**
 * Las dos bandas de la bandeja: «te asignaron una» y «ésta se fue» (DEF-067).
 *
 * POR QUÉ EXISTE (QA de TEST, 07-oct-2026, conversación `c77978ba`)
 * ----------------------------------------------------------------
 * A las 15:49:34Z el reloj del asesor reasignó la conversación lejos de
 * acasallo y la bandeja mostró en ámbar «Ya no está en tu bandeja» —cierto en
 * ese momento—. A las 16:28:48Z un supervisor se la devolvió y apareció en
 * verde «Volvió a tu bandeja: el cliente escribió en una conversación
 * resuelta» —falso: fue una reasignación—. Y las DOS quedaron a la vista a la
 * vez, porque cada banda se prendía por su lado y solo se apagaba con su
 * propio botón. Tres reglas, puras acá y cableadas en `pages/Inbox.tsx`:
 *
 * - **Una banda por conversación, y manda la más reciente.** Que vuelva a mi
 *   lista apaga el ámbar de esa conversación; que se vaya apaga el verde. Dos
 *   conversaciones distintas sí pueden tener una cada una.
 * - **Irse se confirma.** Una respuesta de la lista sin la abierta no alcanza:
 *   hay cinco disparadores que se solapan (montaje, sondeo de 15 s,
 *   `BANDEJA_CAMBIO`, `MENSAJE_NUEVO`, volver a la pestaña) y una respuesta
 *   vieja o un índice que todavía no refleja la asignación se parecen a «se
 *   fue». Hacen falta dos respuestas seguidas sin ella, o que el hilo haya
 *   contestado 403/404 —la prueba de verdad: el servidor dice que ya no es
 *   tuya—. Y cada pedido lleva número: lo que llega después de una respuesta
 *   más nueva, o de una acción propia (soltar, resolver), no se aplica — la
 *   misma idea que `hilo.ts` para el detalle.
 * - **El verde dice por qué llegó.** «Volvió a tu bandeja…» solo si la última
 *   asignación la escribió la reapertura (`assigned_by = reapertura-cliente`,
 *   `conversation/_reabrir_si_estaba_resuelta`); cualquier otra es «Nueva
 *   conversación asignada», el texto de siempre. `reabierta_ts` solo no
 *   alcanza: queda puesto hasta que alguien atienda, también si en el medio
 *   la reasignó un supervisor.
 *
 * Puro: lo corre `tests/unit/test_qa_test_07oct_portal.py` en Node.
 */
import { reabiertaSinAtender } from './atencion'

/** `conversation.ASIGNADA_POR_REAPERTURA`, cruzado por test. */
export const ASIGNADA_POR_REAPERTURA = 'reapertura-cliente'

/** Respuestas seguidas de la lista sin la abierta para darla por ida. */
export const FALTAS_PARA_DAR_POR_IDA = 2

export const TEXTO_NUEVA = 'Nueva conversación asignada'
export const TEXTO_VOLVIO = 'Volvió a tu bandeja: el cliente escribió en una conversación resuelta'

/** Lo que estas reglas leen de una fila de la lista. */
export interface FilaDeLaBandeja {
  conversation_id: string
  status?: string | null
  assigned_by?: string | null
  reabierta_ts?: string | null
  asesor_atendio_ts?: string | null
}

/** Una conversación que salió de la bandeja: porque te la sacaron (`propia:
 *  false`, en ámbar) o porque te saliste tú (en verde, con a dónde fue). */
export interface Salida<T> {
  conv: T
  propia: boolean
  detalle?: string
}

export interface AvisosDeLaBandeja<T> {
  /** La banda verde: la que llegó. */
  nueva: T | null
  /** La banda de la que se fue. */
  salio: Salida<T> | null
  /** Las asignadas de la última respuesta; `null` antes de la primera, que
   *  nunca avisa: al abrir la bandeja todo es «nuevo». */
  conocidas: string[] | null
  /** Asignadas y resueltas de la última respuesta: con ellas se nombra a la
   *  que se fue, que ya no viene en la nueva. */
  anteriores: T[]
  /** Respuestas seguidas sin la abierta, y de cuál se cuentan. */
  faltas: number
  faltasDe: string | null
}

export function avisosIniciales<T>(): AvisosDeLaBandeja<T> {
  return { nueva: null, salio: null, conocidas: null, anteriores: [], faltas: 0, faltasDe: null }
}

/** Una respuesta de `GET /conversations?scope=mine&resueltas=1`. */
export interface LlegadaDeLaLista<T> {
  asignadas: T[]
  resueltas: T[]
  /** La conversación abierta en el chat. */
  abierta: string | null
  /** La que el hilo dio por perdida con un 403/404 (`hilo.esDefinitivo`). */
  fueraConfirmada: string | null
  /** La que quien mira devolvió al agente: si se va, se fue con el agente. */
  devueltaAlAgente: string | null
}

export interface TrasLaLista<T> {
  avisos: AvisosDeLaBandeja<T>
  /** Soltar la abierta: deseleccionarla y limpiar el compositor. */
  soltarLaAbierta: boolean
}

/** ¿Se aplica la respuesta del pedido `numero`? Solo si es más nueva que lo
 *  último que ya se aplicó —una respuesta o una acción propia—. */
export function respuestaVigente(numero: number, ultimaAplicada: number): boolean {
  return numero > ultimaAplicada
}

const idDe = (c: { conversation_id: string }) => c.conversation_id

export function alLlegarLaLista<T extends FilaDeLaBandeja>(
  previo: AvisosDeLaBandeja<T>, llegada: LlegadaDeLaLista<T>,
): TrasLaLista<T> {
  const asignadas = new Set(llegada.asignadas.map(idDe))
  const presentes = new Set([...asignadas, ...llegada.resueltas.map(idDe)])
  let { nueva, salio } = previo

  // Lo que llegó. Una resuelta que el cliente reabrió y volvió a mí entra acá
  // como nueva, y es lo correcto: es trabajo que no estaba.
  if (previo.conocidas) {
    const conocidas = new Set(previo.conocidas)
    const recien = llegada.asignadas.find((c) => !conocidas.has(idDe(c)))
    if (recien) {
      nueva = recien
      if (salio && idDe(salio.conv) === idDe(recien)) salio = null
    }
  }
  // Volvió a mi lista: el ámbar de esa conversación ya no es cierto.
  if (salio && !salio.propia && asignadas.has(idDe(salio.conv))) salio = null

  // La abierta «se fue» solo si no está en ninguna de las dos listas —abrir
  // una resuelta desde su filtro no es perderla— y está confirmado.
  const abierta = llegada.abierta
  let faltas = 0
  let faltasDe: string | null = null
  let soltarLaAbierta = false
  // La que falta se sigue recordando mientras no se confirme que se fue: con
  // qué nombre decirlo sale de la última respuesta en que estuvo.
  let pendiente: T | undefined
  if (abierta && !presentes.has(abierta)) {
    faltas = (previo.faltasDe === abierta ? previo.faltas : 0) + 1
    faltasDe = abierta
    const ida = previo.anteriores.find((c) => idDe(c) === abierta)
    pendiente = ida
    if (faltas >= FALTAS_PARA_DAR_POR_IDA || llegada.fueraConfirmada === abierta) {
      soltarLaAbierta = true
      pendiente = undefined
      faltas = 0
      faltasDe = null
      if (ida) {
        salio = llegada.devueltaAlAgente === abierta
          ? { conv: ida, propia: true, detalle: 'La conduce el agente IA desde donde quedó.' }
          : { conv: ida, propia: false }
        if (nueva && idDe(nueva) === abierta) nueva = null
      }
    }
  }

  return {
    avisos: {
      nueva, salio, faltas, faltasDe,
      conocidas: [...asignadas],
      anteriores: [...llegada.asignadas, ...llegada.resueltas, ...(pendiente ? [pendiente] : [])],
    },
    soltarLaAbierta,
  }
}

/**
 * Quien mira se salió (reasignar o devolver a la cola): la banda verde de
 * «te saliste», y la lista ya sin ella, sin esperar al sondeo.
 */
export function alSoltar<T extends FilaDeLaBandeja>(
  previo: AvisosDeLaBandeja<T>, ida: T | undefined, detalle: string, restantes: T[],
): AvisosDeLaBandeja<T> {
  const id = ida ? idDe(ida) : null
  return {
    ...previo,
    nueva: previo.nueva && idDe(previo.nueva) === id ? null : previo.nueva,
    salio: ida ? { conv: ida, propia: true, detalle } : previo.salio,
    conocidas: restantes.map(idDe),
    anteriores: previo.anteriores.filter((c) => idDe(c) !== id),
    faltas: 0,
    faltasDe: null,
  }
}

/**
 * Quien mira la resolvió: sale de las asignadas sin que eso sea «se fue», y
 * si el cliente la reabre y vuelve, es nueva otra vez.
 */
export function alResolver<T extends FilaDeLaBandeja>(
  previo: AvisosDeLaBandeja<T>, id: string,
): AvisosDeLaBandeja<T> {
  return {
    ...previo,
    nueva: previo.nueva && idDe(previo.nueva) === id ? null : previo.nueva,
    salio: previo.salio && idDe(previo.salio.conv) === id ? null : previo.salio,
    conocidas: previo.conocidas ? previo.conocidas.filter((c) => c !== id) : null,
    faltas: 0,
    faltasDe: null,
  }
}

/** ¿Llegó porque el cliente escribió sobre una resuelta? */
export function volvioPorElCliente(conv: FilaDeLaBandeja): boolean {
  return (conv.assigned_by || '') === ASIGNADA_POR_REAPERTURA && reabiertaSinAtender(conv)
}

/** El título de la banda verde. */
export function textoDeLaNueva(conv: FilaDeLaBandeja): string {
  return volvioPorElCliente(conv) ? TEXTO_VOLVIO : TEXTO_NUEVA
}
